import Fuse from 'fuse.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CallerIdPicker from '../../shared/components/CallerIdPicker';
import ExtensionPill from '../../shared/components/ExtensionPill';
import { isElectron } from '../../shared/helpers/platform';
import { handleEnterPress, isVcrmSupportedOs } from '../../shared/helpers/util';
import { getContactNumber } from '../../shared/services/contactsService';
import Contact from '../components/Contact';
import {
  getAudioDevices,
  testCanChangeAudioDevice,
} from '../helpers/getAudioDevices';
import dtmfTones from '../helpers/tones';
import { diallerNumberSet, dndSet } from '../reducers/app';
import {
  cancelTransfer,
  sendDTMF,
  startCall,
  transferCall,
} from '../reducers/calls';
import { combinedContactsSelector } from '../selectors/contacts';
import { StoreDispatch, StoreState } from '../store';
import * as styles from '../styles/dialler.module.scss';
import { BackspaceIcon, PhoneIcon, SearchIcon } from './icons';
import VcrmIndicator from './VcrmIndicator';

const asterisk = String.fromCharCode(10033);
const noBreakSpace = String.fromCharCode(160);

const KEYS = [
  { display: '1', other: noBreakSpace },
  { display: '2', other: 'abc' },
  { display: '3', other: 'def' },
  { display: '4', other: 'ghi' },
  { display: '5', other: 'jkl' },
  { display: '6', other: 'mno' },
  { display: '7', other: 'pqrs' },
  { display: '8', other: 'tuv' },
  { display: '9', other: 'wxyz' },
  { display: asterisk, value: '*', other: noBreakSpace },
  { display: '0', other: '+' },
  { display: '#', other: noBreakSpace },
];

const ALLOWED_CHARS = KEYS.map((k) => k.value || k.display);

const isNumeric = (input: string) => {
  return input.split('').every((x) => ALLOWED_CHARS.includes(x));
};

interface Props {
  inCall?: boolean;
  transfer?: boolean;
}

const Dialler: React.FC<Props> = (props) => {
  const dispatch = useDispatch<StoreDispatch>();
  const contacts = useSelector(combinedContactsSelector);
  const call = useSelector((state: StoreState) =>
    state.calls.find((c) => c.active === true),
  );
  const callId = call?.id;
  const tones = call?.tones || [];
  const extensionStatus = useSelector(
    (state: StoreState) => state.extension.status,
  );
  const dnd = useSelector((state: StoreState) => state.app.dnd);
  const doSetDnd = useCallback(
    (value: boolean) => dispatch(dndSet(value)),
    [dispatch],
  );
  const selectedAudioInputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedAudioInputDeviceId,
  );
  const selectedAudioOutputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedAudioOutputDeviceId,
  );
  const selectedRingingOutputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedRingingOutputDeviceId,
  );
  const hasGrantedAudio = useSelector(
    (state: StoreState) => state.audio.hasGrantedAudio,
  );
  const vcrmLaunchToken = useSelector(
    (state: StoreState) => state.vcrm.launchToken,
  );
  const inputValue = useSelector(
    (state: StoreState) => state.app.diallerNumber,
  );

  const setInputValue = useCallback(
    (number: string) => {
      dispatch(diallerNumberSet({ number }));
    },
    [dispatch],
  );

  const keyPress = (id: string, tone: string) => {
    dispatch(sendDTMF({ id, tone }));
  };
  const callClick = (
    number: string,
    type?: string,
    name?: string,
    description?: string,
    email?: string,
  ) => {
    dispatch(startCall({ number, type, name, description, email }));
  };
  const transferClick = (callId: string, number: string) => {
    dispatch(transferCall({ id: callId, number }));
  };
  const transferCancelClick = (id: string) => {
    dispatch(cancelTransfer({ id }));
  };

  // const [inputValue, setInputValue] = useState('');
  const [focussedIdx, setFocussedIdx] = useState<number | null>(null);
  const [selectedAudioInputDevice, setSelectedAudioInputDevice] =
    useState<any>(null);
  const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] =
    useState<any>(null);
  const [canChangeAudioDevice, setCanChangeAudioDevice] = useState(false);
  const [showContacts, setShowContacts] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollListRef = useRef<HTMLUListElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const diallerInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const focusInput = (e: KeyboardEvent) => {
      // mod+/ handler
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        // Focus the dialler input
        diallerInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', focusInput);

    const canChangeAudioDevice = testCanChangeAudioDevice();
    getAudioDevices({
      selectedAudioInputDeviceId,
      selectedAudioOutputDeviceId,
      selectedRingingOutputDeviceId,
    })
      .then((devices) => {
        const { selectedAudioInputDevice, selectedAudioOutputDevice } = devices;
        setSelectedAudioInputDevice(selectedAudioInputDevice);
        setSelectedAudioOutputDevice(selectedAudioOutputDevice);
        setCanChangeAudioDevice(canChangeAudioDevice);
      })
      .catch(() => {
        setSelectedAudioInputDevice(null);
        setSelectedAudioOutputDevice(null);
        setCanChangeAudioDevice(canChangeAudioDevice);
      });

    return () => {
      document.removeEventListener('keydown', focusInput);
    };
  }, [
    selectedAudioInputDeviceId,
    selectedAudioOutputDeviceId,
    selectedRingingOutputDeviceId,
  ]);

  const onKeyPress = (digit: string) => {
    // On dialler key press

    const { inCall } = props;

    if (inCall) {
      // FIXME
      const audio = dtmfTones[digit as keyof typeof dtmfTones];
      audio.play();
      if (callId) {
        keyPress(callId, digit);
      }
      return;
    }

    // If not in call, append digit to inputValue and set it
    const newInputValue = inputValue + digit;

    setInputValue(newInputValue);
    setShowContacts(false);
  };

  const onInputChange = (value: string) => {
    // remove all whitespace on input and on paste
    value = value.replace(/ /g, '');
    // Force lower case input
    setInputValue(value);
    setFocussedIdx(null);
    setShowContacts(true);
  };

  const onBackspacePress = () => {
    // On backspace icon press
    // Remove last character from inputValue and set it
    const newInputValue = inputValue.slice(0, -1);
    setInputValue(newInputValue);
  };

  const focusNext = (contactCount: number) => {
    let newFocussedIdx: number | null;
    if (focussedIdx === null) {
      newFocussedIdx = 0;
    } else {
      newFocussedIdx = focussedIdx + 1;
      newFocussedIdx =
        (newFocussedIdx as number) > contactCount - 1 ? null : newFocussedIdx;
    }

    setFocussedIdx(newFocussedIdx);
  };

  const focusPrev = (contactCount: number) => {
    let newFocussedIdx: number | null;
    if (focussedIdx === null) {
      newFocussedIdx = contactCount - 1;
    } else {
      newFocussedIdx = focussedIdx - 1;
      newFocussedIdx = (newFocussedIdx as number) < 0 ? null : newFocussedIdx;
    }
    setFocussedIdx(newFocussedIdx);
  };

  const sendCall = (number: string) => {
    const { transfer } = props;

    // If there's no number, don't bother
    if (number.length === 0) {
      return;
    }

    // Clear the dialler/search field now the call has been placed, ready for
    // the next number (covers transfer, contact match and basic call paths).
    const resetInput = () => {
      setInputValue('');
      setFocussedIdx(null);
      setShowContacts(false);
    };

    // If we're transferring, just send it
    if (transfer && callId) {
      transferClick(callId, number);
      resetInput();
      return;
    }

    // Lookup entered info to see if it matches any known contact
    // If it does, initiate the call with more detailed info
    const found = contacts?.some((c) => {
      const contactNumber = getContactNumber(c);
      if (contactNumber === number) {
        callClick(
          contactNumber,
          c.type,
          c.name,
          contactNumber,
          (c as any).email,
        );
        return true;
      }
      return false;
    });

    // If not, just send a basic call
    if (!found) {
      callClick(number);
    }

    resetInput();
  };

  const onInputKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    filteredContacts: any[],
  ) => {
    const ESCAPE = 27;
    const contactCount = filteredContacts.length;

    if (e.keyCode === ESCAPE) {
      // On escape press, clear input
      setInputValue('');
      setFocussedIdx(null);
      setShowContacts(false);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusPrev(contactCount);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusNext(contactCount);
      return;
    }

    if (e.key === 'Enter') {
      // On enter press, send call if is numeric
      // If the dialler input value is numeric, send the call
      // (sendCall clears the field once the call is placed)
      if (focussedIdx !== null) {
        const filteredContact = filteredContacts[focussedIdx];
        const number =
          filteredContact.shortNumber || filteredContact.phoneNumber;
        sendCall(number);
      } else if (isNumeric(inputValue)) {
        sendCall(inputValue);
      } else if (filteredContacts.length === 1) {
        const filteredContact = filteredContacts[0];
        const number =
          filteredContact.shortNumber || filteredContact.phoneNumber;
        sendCall(number);
      }
    }
  };

  useEffect(() => {
    if (!contactRef.current) {
      // No filtered contacts exist, just return
      return;
    }

    const containerHeight = scrollContainerRef.current?.clientHeight || 0;
    const itemHeight = contactRef.current?.clientHeight || 0;
    const itemTop = (focussedIdx || 0) * itemHeight;
    const itemBottom = itemTop + itemHeight;
    const scrollTop = scrollContainerRef.current?.scrollTop || 0;
    const scrollBottom = (scrollTop || 0) + (containerHeight || 0);
    const listHeight = scrollListRef.current?.clientHeight;

    if (scrollContainerRef.current) {
      if (itemTop === 0) {
        // If the item is at the top of the container
        // Set the scroll to the top of the container
        scrollContainerRef.current.scrollTop = 0;
      } else if (itemBottom === listHeight) {
        // If the item is at the bottom of the container
        // Set the scroll to the bottom of the list
        scrollContainerRef.current.scrollTop = listHeight;
      } else if (itemTop < scrollTop) {
        // If the item is above the visible container
        // Set the scroll to the top of the item
        scrollContainerRef.current.scrollTop = itemTop;
      } else if (itemBottom > scrollBottom) {
        // If the item is below the visible container
        // Set the scroll so the item sits at the bottom of the container
        scrollContainerRef.current.scrollTop = itemBottom - containerHeight;
      }
    }
  }, [focussedIdx]);

  const getMatches = (contacts: any[] = [], searchPhrase: string) => {
    const fuse = new Fuse(contacts, {
      keys: [
        { name: 'name', weight: 1 },
        { name: 'shortNumber', weight: 0.8 },
        { name: 'phoneNumber', weight: 0.8 },
      ],
      threshold: 0.4,
    });

    // Remove () + and - from string
    return fuse.search(searchPhrase.replace(/[-+()\s]/g, ''));
  };

  const sendTransfer = (callId: string, inputValue: string) => {
    // If there's no number, don't bother
    if (inputValue.length === 0) {
      return;
    }

    transferClick(callId, inputValue);
  };

  const doCancelTransfer = (callId: string) => {
    setInputValue('');
    transferCancelClick(callId);
  };

  let filteredContacts: any[] = [];

  const normalizedInputValue = inputValue.toLowerCase().trim();

  // If the value entered is not numeric, search contacts for matches
  if (normalizedInputValue) {
    filteredContacts = getMatches(contacts, normalizedInputValue).map(
      (match) => match.item,
    );
  }

  const contactsVisible =
    showContacts &&
    (filteredContacts.length > 0 || normalizedInputValue.length > 0);

  const showBackspace = inputValue && !showContacts;
  const showSearch = !inputValue;

  const { inCall, transfer } = props;

  let bottomElement;
  if (inCall) {
    bottomElement = '';
  } else if (transfer) {
    const transferClicked = () => {
      if (callId) {
        sendTransfer(callId, inputValue);
      }
    };

    const cancelClicked = () => {
      if (callId) {
        doCancelTransfer(callId);
      }
    };
    bottomElement = (
      <div className={styles.button_container}>
        <div
          className={styles.transfer_button}
          onClick={transferClicked}
          onKeyDown={handleEnterPress(transferClicked)}
          role='button'
          tabIndex={0}
        >
          <span className={styles.button_label}>Transfer</span>
        </div>
        <div
          className={styles.cancel_button}
          onClick={cancelClicked}
          onKeyDown={handleEnterPress(cancelClicked)}
          role='button'
          tabIndex={0}
        >
          <span className={styles.button_label}>Cancel</span>
        </div>
      </div>
    );
  } else {
    const callClicked = () => {
      sendCall(inputValue);
    };
    bottomElement = (
      <div className={styles.button_container}>
        {!hasGrantedAudio &&
        canChangeAudioDevice &&
        (!selectedAudioInputDevice || !selectedAudioOutputDevice) ? (
          <div
            className={styles.button_disabled}
            title='No audio input/output device connected'
          >
            <PhoneIcon />
          </div>
        ) : (
          <div
            className={styles.button}
            onClick={callClicked}
            onKeyDown={handleEnterPress(callClicked)}
            role='button'
            tabIndex={0}
          >
            <PhoneIcon />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.top_container}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          className={styles.input_container}
          onClick={(e) => {
            if (!inCall && (e.target as HTMLElement)?.id !== 'backspace') {
              diallerInputRef.current?.focus();
            }
          }}
        >
          {inCall ? (
            <div className={styles.display}>
              {tones.slice().reverse().join('')}
            </div>
          ) : (
            <>
              {showSearch && <SearchIcon className={styles.search_icon} />}
              <input
                className={styles.input}
                value={inputValue}
                onChange={() => {
                  onInputChange(diallerInputRef.current?.value || '');
                }}
                onKeyDown={(e) => onInputKeyPress(e, filteredContacts)}
                ref={diallerInputRef}
                spellCheck='false'
                disabled={!hasGrantedAudio}
              />
              {showBackspace && (
                <BackspaceIcon
                  className={styles.backspace}
                  onClick={() => onBackspacePress()}
                />
              )}
            </>
          )}
        </div>
        <div className={styles.extension_container}>
          {/* <div className={styles.extension_details}>
              <span className={styles.extension_name}>{extensionName}</span>
              <span className={styles.extension_number}>{extensionShortNumber}</span>
          </div> */}
          {/* <NvSelectDropdown
            type='inline'
            color={{
              text: 'var(--primary)',
            }}
            isClearable={false}
            isSearchable={false}
            className={styles.user_dropdown}
            options={availableExtensionsOptions}
            defaultValue={`${extensionShortNumber} - ${extensionName}`}
            onChange={(selected) => {
              doSwitchExtension(selected[0].value);
            }}
            menuAlign='right'
            selectStyles={{
              singleValue: (base) => ({
                ...base,
                color: 'var(--white-color)',
              }),
              menu: (base) => ({
                ...base,
                width: 'max-content',
              }),
              dropdownIndicator: (base) => ({
                ...base,
                color: 'var(--white-color)',
                '&:hover': {
                  color: 'var(--white-color)',
                },
              }),
            }}
          /> */}
          <ExtensionPill
            extensionStatus={extensionStatus}
            dnd={dnd}
            onDndChange={doSetDnd}
          />
          {isElectron() && vcrmLaunchToken && isVcrmSupportedOs() && (
            <VcrmIndicator className={styles.vcrmIndicator} />
          )}
        </div>
      </div>
      <div
        className={
          contactsVisible
            ? styles.content_container_contacts
            : styles.content_container
        }
      >
        <div className={styles.contacts_container} ref={scrollContainerRef}>
          <ul className={styles.contacts_list} ref={scrollListRef}>
            {filteredContacts.length > 0 ? (
              filteredContacts.map((contact, i) => {
                const number = contact.shortNumber || contact.phoneNumber;
                const focussed = focussedIdx === i;
                return (
                  <div key={i} ref={contactRef}>
                    <Contact
                      name={contact.name}
                      description={number}
                      email={contact.email}
                      id={contact.id}
                      number={number}
                      type={contact.type}
                      status={contact.status}
                      focussed={focussed}
                    />
                  </div>
                );
              })
            ) : (
              <li className={styles.no_contacts}>No matching contacts found</li>
            )}
          </ul>
        </div>
        <div className={styles.key_container}>
          {KEYS.map((k, i) => (
            <div
              className={`${styles.key} ${!hasGrantedAudio && styles.disabled}`}
              onClick={() => onKeyPress(k.value || k.display)}
              key={i}
              onKeyDown={handleEnterPress(() =>
                onKeyPress(k.value || k.display),
              )}
              role='button'
              tabIndex={0}
            >
              <span className={styles.key_digit}>{k.display}</span>
              <span className={styles.key_other}>{k.other.toUpperCase()}</span>
            </div>
          ))}
        </div>
      </div>{' '}
      <div className={styles.callerPickerContainer}>
        <div className={styles.callerPickerTextContainer}>
          <span className={styles.text_black}>Calling from:</span>
          <CallerIdPicker inline />
        </div>
      </div>
      {bottomElement}
    </div>
  );
};

export default Dialler;
